// CR075: Batch Runs Orchestrator - Main Admin Page
// CR554: Bulk select + bulk actions (soft delete, restore, hard delete)
// CR555: Pagination + "Select all matching" cross-page bulk actions
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Plus, RefreshCw, ChevronRight, ChevronLeft, Clock, CheckCircle2, XCircle, AlertCircle,
  Loader2, Archive, Package, X, Calendar, PauseCircle, Trash2, RotateCcw, AlertTriangle,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { CreateBatchRunWizard } from "@/components/admin/batch-runs/CreateBatchRunWizard";
import { RulePacksManagerDialog } from "@/components/admin/batch-runs/rule-packs";
import { computeEverStagedFromAttempts, EVER_STAGED_STEP_KEYS } from "@/lib/batch-runs/everStaged";

interface BatchRun {
  id: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  status: string;
  config_json: Record<string, unknown>;
  merchants_in_scope: number | null;
  sources_enabled: string[];
  total_staged_items: number;
  ever_staged_items?: number;
  ever_staged_updated_at?: string | null;
  normalized_count: number;
  deduped_count: number;
  published_count: number;
  rejected_count: number;
  error_count: number;
  is_deleted: boolean;
  deleted_at: string | null;
}

function getRunMerchantsCount(run: BatchRun): number {
  if (typeof run.merchants_in_scope === "number" && run.merchants_in_scope > 0) {
    return run.merchants_in_scope;
  }
  const cfg = run.config_json ?? {};
  const merchants = (cfg.merchants as unknown) ?? (cfg.merchant_ids as unknown);
  if (Array.isArray(merchants)) {
    return merchants.length;
  }
  return 0;
}

interface RunCounts {
  active: number;
  deleted: number;
  total: number;
}

const PAGE_SIZES = [25, 50, 100];

const statusConfig: Record<string, { label: string; icon: React.ReactNode; variant: "default" | "secondary" | "destructive" | "outline" | "warning" }> = {
  CREATED: { label: "Created", icon: <Clock className="h-3 w-3" />, variant: "secondary" },
  RUNNING: { label: "Running", icon: <Loader2 className="h-3 w-3 animate-spin" />, variant: "default" },
  NEEDS_REVIEW: { label: "Needs Review", icon: <PauseCircle className="h-3 w-3" />, variant: "warning" },
  COMPLETE: { label: "Complete", icon: <CheckCircle2 className="h-3 w-3" />, variant: "outline" },
  FAILED: { label: "Failed", icon: <XCircle className="h-3 w-3" />, variant: "destructive" },
  CANCELLED: { label: "Cancelled", icon: <AlertCircle className="h-3 w-3" />, variant: "secondary" },
  PARTIAL: { label: "Partial", icon: <AlertCircle className="h-3 w-3" />, variant: "secondary" },
};

export default function BatchRuns() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const [runs, setRuns] = useState<BatchRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [showRulePacksManager, setShowRulePacksManager] = useState(false);
  const [counts, setCounts] = useState<RunCounts | null>(null);
  const [everStagedByRunId, setEverStagedByRunId] = useState<Record<string, number>>({});

  // CR555: Pagination from URL
  // CR558: deletedOnly filter — when clicking "Deleted: N" pill
  const showDeleted = searchParams.get("showDeleted") === "1";
  const deletedOnly = searchParams.get("deletedOnly") === "1";
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
  const pageSize = PAGE_SIZES.includes(parseInt(searchParams.get("pageSize") || "25", 10))
    ? parseInt(searchParams.get("pageSize") || "25", 10) : 25;
  const scheduleRunIdFilter = searchParams.get("schedule_run_id");

  // CR555: Total count for current filter (for pagination)
  const [filteredTotal, setFilteredTotal] = useState(0);

  // CR554: Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [hardDeleteDialogOpen, setHardDeleteDialogOpen] = useState(false);
  const [hardDeleteConfirmText, setHardDeleteConfirmText] = useState("");
  const [hardDeleteAcknowledged, setHardDeleteAcknowledged] = useState(false);

  const matchingCount = useMemo(() => {
    if (deletedOnly) return counts?.deleted ?? 0;
    if (showDeleted) return counts?.total ?? 0;
    return counts?.active ?? 0;
  }, [showDeleted, deletedOnly, counts]);

  // URL helpers
  const updateParams = useCallback((updates: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => {
      if (v === null) next.delete(k); else next.set(k, v);
    });
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const setPage = (p: number) => updateParams({ page: String(p) });
  const setPageSize = (s: number) => updateParams({ pageSize: String(s), page: "1" });
  const setShowDeleted = (v: boolean) => {
    updateParams({ showDeleted: v ? "1" : null, deletedOnly: null, page: "1" });
    clearSelection();
  };
  const setDeletedOnly = (v: boolean) => {
    updateParams({ deletedOnly: v ? "1" : null, showDeleted: v ? "1" : null, page: "1" });
    clearSelection();
  };
  const setActiveOnly = () => {
    updateParams({ showDeleted: null, deletedOnly: null, page: "1" });
    clearSelection();
  };
  const clearScheduleRunFilter = () => updateParams({ schedule_run_id: null, page: "1" });

  const totalPages = Math.max(1, Math.ceil(filteredTotal / pageSize));
  const rangeStart = (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, filteredTotal);

  const fetchCounts = useCallback(async () => {
    const { data } = await supabase.rpc("batch_runs_counts");
    if (data) setCounts(data as unknown as RunCounts);
  }, []);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      // Get total count for pagination
      let countQuery = supabase
        .from("cr075_batch_runs")
        .select("id", { count: "exact", head: true });

      if (deletedOnly) {
        countQuery = countQuery.eq("is_deleted", true);
      } else if (!showDeleted) {
        countQuery = countQuery.eq("is_deleted", false);
      }
      if (scheduleRunIdFilter) countQuery = countQuery.eq("schedule_run_id", scheduleRunIdFilter);

      const { count: totalCount } = await countQuery;
      setFilteredTotal(totalCount ?? 0);

      // Get paginated data
      const offset = (page - 1) * pageSize;
      let query = supabase
        .from("cr075_batch_runs")
        .select("*")
        .order("created_at", { ascending: false })
        .range(offset, offset + pageSize - 1);

      if (deletedOnly) {
        query = query.eq("is_deleted", true);
      } else if (!showDeleted) {
        query = query.eq("is_deleted", false);
      }
      if (scheduleRunIdFilter) query = query.eq("schedule_run_id", scheduleRunIdFilter);

      const { data, error } = await query;
      if (error) throw error;
      const pageRuns = (data || []) as BatchRun[];
      setRuns(pageRuns);

      // "Ever staged" fallback for pre-migration runs:
      // max records_out over staging-producing terminal attempts (never a sum).
      if (pageRuns.length === 0) {
        setEverStagedByRunId({});
        return;
      }

      const runIds = pageRuns.map((r) => r.id);
      const { data: attempts, error: attemptsError } = await supabase
        .from("cr075_batch_run_step_attempts")
        .select("batch_run_id, step_key, records_out, merchant_source_id, status")
        .in("batch_run_id", runIds)
        .in("step_key", [...EVER_STAGED_STEP_KEYS])
        .is("merchant_source_id", null);

      if (attemptsError) {
        console.error("Failed to fetch batch run attempts for ever-staged counts:", attemptsError);
        setEverStagedByRunId({});
      } else {
        const attemptsByRun: Record<string, Array<{
          step_key: string | null;
          records_out: number | null;
          merchant_source_id: string | null;
          status: string | null;
        }>> = {};

        for (const attempt of attempts || []) {
          const runId = attempt.batch_run_id;
          if (!attemptsByRun[runId]) attemptsByRun[runId] = [];
          attemptsByRun[runId].push({
            step_key: attempt.step_key,
            records_out: attempt.records_out,
            merchant_source_id: attempt.merchant_source_id,
            status: attempt.status,
          });
        }

        const computed: Record<string, number> = {};
        for (const run of pageRuns) {
          computed[run.id] = computeEverStagedFromAttempts(attemptsByRun[run.id] || []);
        }
        setEverStagedByRunId(computed);
      }
    } catch (err) {
      console.error("Failed to fetch batch runs:", err);
      toast({ title: "Error", description: "Failed to load batch runs", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [showDeleted, deletedOnly, scheduleRunIdFilter, page, pageSize, toast]);

  useEffect(() => {
    fetchRuns();
    fetchCounts();
  }, [fetchRuns, fetchCounts]);

  const handleRunCreated = (runId: string) => {
    setShowCreateWizard(false);
    navigate(`/admin/batch-runs/${runId}`);
  };

  // Selection helpers
  const allPageIds = useMemo(() => runs.map(r => r.id), [runs]);
  const allPageSelected = runs.length > 0 && allPageIds.every(id => selectedIds.has(id));

  const toggleOne = (id: string) => {
    setSelectAllMatching(false);
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllPage = () => {
    setSelectAllMatching(false);
    if (allPageSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allPageIds));
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectAllMatching(false);
  };

  const handleSelectAllMatching = () => {
    setSelectAllMatching(true);
    setSelectedIds(new Set(allPageIds)); // visually check current page
  };

  const effectiveSelectedCount = selectAllMatching ? matchingCount : selectedIds.size;
  const someSelected = effectiveSelectedCount > 0;

  // Determine what's selected for action buttons
  const selectedRuns = useMemo(() => runs.filter(r => selectedIds.has(r.id)), [runs, selectedIds]);
  const allSelectedAreDeleted = selectAllMatching
    ? deletedOnly || showDeleted
    : selectedRuns.length > 0 && selectedRuns.every(r => r.is_deleted);
  const hasDeletedInSelection = selectAllMatching
    ? deletedOnly || showDeleted
    : selectedRuns.some(r => r.is_deleted);
  const hasActiveInSelection = selectAllMatching
    ? !deletedOnly && (!showDeleted || selectedRuns.some(r => !r.is_deleted))
    : selectedRuns.some(r => !r.is_deleted);

  // Bulk actions
  const handleBulkSoftDelete = async () => {
    setBulkActionLoading(true);
    try {
      let result: any;
      if (selectAllMatching) {
        const { data, error } = await supabase.rpc("batch_runs_bulk_soft_delete_matching", {
          p_schedule_run_id: scheduleRunIdFilter || undefined,
        });
        if (error) throw error;
        result = data;
      } else {
        const activeIds = selectedRuns.filter(r => !r.is_deleted).map(r => r.id);
        if (activeIds.length === 0) return;
        const { data, error } = await supabase.rpc("batch_runs_bulk_soft_delete", { batch_run_ids: activeIds });
        if (error) throw error;
        result = data;
      }
      toast({ title: "Deleted", description: `Soft deleted ${result?.updated_count ?? 0} runs` });
      clearSelection();
      fetchRuns();
      fetchCounts();
    } catch (err) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Bulk delete failed", variant: "destructive" });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkRestore = async () => {
    setBulkActionLoading(true);
    try {
      let result: any;
      if (selectAllMatching) {
        const { data, error } = await supabase.rpc("batch_runs_bulk_restore_matching", {
          p_schedule_run_id: scheduleRunIdFilter || undefined,
        });
        if (error) throw error;
        result = data;
      } else {
        const deletedIds = selectedRuns.filter(r => r.is_deleted).map(r => r.id);
        if (deletedIds.length === 0) return;
        const { data, error } = await supabase.rpc("batch_runs_bulk_restore", { batch_run_ids: deletedIds });
        if (error) throw error;
        result = data;
      }
      toast({ title: "Restored", description: `Restored ${result?.updated_count ?? 0} runs` });
      clearSelection();
      fetchRuns();
      fetchCounts();
    } catch (err) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Bulk restore failed", variant: "destructive" });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkHardDelete = async () => {
    setBulkActionLoading(true);
    try {
      let result: any;
      if (selectAllMatching) {
        const { data, error } = await supabase.rpc("batch_runs_bulk_hard_delete_matching", {
          p_schedule_run_id: scheduleRunIdFilter || undefined,
        });
        if (error) throw error;
        result = data;
      } else {
        const deletedIds = selectedRuns.filter(r => r.is_deleted).map(r => r.id);
        if (deletedIds.length === 0) return;
        const { data, error } = await supabase.rpc("batch_runs_bulk_hard_delete", { batch_run_ids: deletedIds });
        if (error) throw error;
        result = data;
      }
      toast({ title: "Hard Deleted", description: `Permanently removed ${result?.deleted_runs ?? 0} runs` });
      clearSelection();
      setHardDeleteDialogOpen(false);
      setHardDeleteConfirmText("");
      fetchRuns();
      fetchCounts();
    } catch (err) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Hard delete failed", variant: "destructive" });
    } finally {
      setBulkActionLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Batch Runs</h1>
            <p className="text-muted-foreground">
              Orchestrate deal collection across Flyers, Website Prompt Packs, and Forum Signals
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowRulePacksManager(true)}>
              <Package className="h-4 w-4 mr-2" />
              Manage Rule Packs
            </Button>
            <Button variant="outline" size="sm" onClick={() => { fetchRuns(); fetchCounts(); }} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button onClick={() => setShowCreateWizard(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Batch Run
            </Button>
          </div>
        </div>

        {/* CR558: Clickable counts strip */}
        {counts && (
          <div className="flex items-center gap-4 text-sm">
            <button
              onClick={setActiveOnly}
              className={`transition-colors ${!showDeleted && !deletedOnly ? "text-foreground font-semibold" : "text-muted-foreground hover:text-foreground cursor-pointer"}`}
            >
              Active: <span className="font-semibold">{counts.active}</span>
            </button>
            <span className="text-muted-foreground/40">|</span>
            <button
              onClick={() => setDeletedOnly(true)}
              className={`transition-colors ${deletedOnly ? "text-foreground font-semibold" : "text-muted-foreground hover:text-foreground cursor-pointer"}`}
            >
              Deleted: <span className="font-semibold">{counts.deleted}</span>
            </button>
            <span className="text-muted-foreground/40">|</span>
            <span className="text-muted-foreground">
              Total: <span className="font-semibold text-foreground">{counts.total}</span>
            </span>
          </div>
        )}

        {/* Schedule Run Filter Pill */}
        {scheduleRunIdFilter && (
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="gap-1.5 py-1 px-2">
              <Calendar className="h-3 w-3" />
              Schedule Run: {scheduleRunIdFilter.slice(0, 8)}...
              <Button variant="ghost" size="sm" className="h-4 w-4 p-0 hover:bg-transparent" onClick={clearScheduleRunFilter}>
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          </div>
        )}

        {/* CR554/555: Bulk actions toolbar — sticky */}
        {someSelected && (
          <div className="sticky top-0 z-10 bg-muted/95 backdrop-blur border rounded-lg p-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant="secondary" className="font-mono">
                {selectAllMatching
                  ? `All ${matchingCount} matching selected`
                  : `${selectedIds.size} selected`}
              </Badge>
              {/* CR555: "Select all matching" prompt */}
              {!selectAllMatching && allPageSelected && matchingCount > runs.length && (
                <Button variant="link" size="sm" className="text-xs h-auto p-0" onClick={handleSelectAllMatching}>
                  Select all {matchingCount} matching runs
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={clearSelection} className="text-xs">
                Clear
              </Button>
            </div>
            <div className="flex items-center gap-2">
              {hasActiveInSelection && !selectAllMatching && (
                <Button variant="outline" size="sm" disabled={bulkActionLoading} onClick={handleBulkSoftDelete} className="gap-1.5">
                  {bulkActionLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  Delete
                </Button>
              )}
              {selectAllMatching && !showDeleted && (
                <Button variant="outline" size="sm" disabled={bulkActionLoading} onClick={handleBulkSoftDelete} className="gap-1.5">
                  {bulkActionLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  Delete All ({matchingCount})
                </Button>
              )}
              {hasDeletedInSelection && (
                <Button variant="outline" size="sm" disabled={bulkActionLoading} onClick={handleBulkRestore} className="gap-1.5">
                  {bulkActionLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                  Restore{selectAllMatching ? ` All (${matchingCount})` : ""}
                </Button>
              )}
              {(allSelectedAreDeleted || (selectAllMatching && showDeleted)) && (
                <Button variant="destructive" size="sm" disabled={bulkActionLoading} onClick={() => setHardDeleteDialogOpen(true)} className="gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Hard Delete{selectAllMatching ? ` All (${matchingCount})` : ` (${selectedIds.size})`}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Runs List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{deletedOnly ? "Deleted Batch Runs" : scheduleRunIdFilter ? "Scheduled Batch Runs" : "Recent Batch Runs"}</CardTitle>
                <CardDescription>
                  {deletedOnly
                    ? "Showing soft-deleted runs. Select runs to restore or permanently delete."
                    : scheduleRunIdFilter
                    ? `Showing batch runs created by schedule run ${scheduleRunIdFilter.slice(0, 8)}...`
                    : "View and manage batch run executions"}
                </CardDescription>
              </div>
              <div className="flex items-center space-x-2">
                <Switch id="show-deleted" checked={showDeleted} onCheckedChange={setShowDeleted} />
                <Label htmlFor="show-deleted" className="text-sm text-muted-foreground">Show deleted</Label>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
              </div>
            ) : runs.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <p>No batch runs found.</p>
              </div>
            ) : (
              <div className="space-y-1">
                {/* Header row */}
                <div className="flex items-center gap-3 px-4 py-2 text-xs text-muted-foreground font-medium border-b">
                  <Checkbox checked={allPageSelected} onCheckedChange={toggleAllPage} aria-label="Select all on page" />
                  <span className="flex-1">Run</span>
                  <span className="w-20 text-right">Merchants</span>
                  <span
                    className="w-24 text-right"
                    title="Highest single-attempt staged count observed for this run."
                  >
                    Ever Staged
                  </span>
                  <span className="w-16 text-right">Published</span>
                  <span className="w-6" />
                </div>

                {runs.map((run) => {
                  const statusInfo = statusConfig[run.status] || statusConfig.CREATED;
                  const isSelected = selectAllMatching || selectedIds.has(run.id);
                  const merchantsCount = getRunMerchantsCount(run);
                  const everStaged = Math.max(
                    run.ever_staged_items || 0,
                    run.total_staged_items || 0,
                    everStagedByRunId[run.id] || 0,
                  );
                  return (
                    <div
                      key={run.id}
                      className={`flex items-center gap-3 p-4 rounded-lg border transition-colors ${
                        isSelected ? "bg-accent/60 border-accent" : "bg-card hover:bg-accent/30"
                      }`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => {
                          if (selectAllMatching) {
                            // Deselect from "all matching" → switch to explicit mode minus this one
                            setSelectAllMatching(false);
                            const next = new Set(allPageIds);
                            next.delete(run.id);
                            setSelectedIds(next);
                          } else {
                            toggleOne(run.id);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select run ${run.id.slice(0, 8)}`}
                      />
                      <button onClick={() => navigate(`/admin/batch-runs/${run.id}`)} className="flex-1 text-left min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Batch Run {run.id.slice(0, 8)}</span>
                          <Badge variant={statusInfo.variant} className="gap-1">{statusInfo.icon}{statusInfo.label}</Badge>
                          {run.is_deleted && (
                            <Badge variant="secondary" className="gap-1"><Archive className="h-3 w-3" />Deleted</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-0.5">
                          <span>Sources: {run.sources_enabled.length > 0 ? run.sources_enabled.join(", ") : "None configured"}</span>
                          <span>{formatDistanceToNow(new Date(run.created_at), { addSuffix: true })}</span>
                        </div>
                      </button>
                      <div className="flex items-center gap-4 shrink-0">
                        <div className="text-right text-sm w-20">
                          <div className="text-muted-foreground text-xs">Merchants</div>
                          <div className="font-medium">{merchantsCount}</div>
                        </div>
                        <div className="text-right text-sm w-24">
                          <div
                            className="text-muted-foreground text-xs"
                            title="Highest single-attempt staged count observed for this run."
                          >
                            Ever Staged
                          </div>
                          <div className="font-medium">{everStaged}</div>
                        </div>
                        <div className="text-right text-sm w-16">
                          <div className="text-muted-foreground text-xs">Published</div>
                          <div className="font-medium">{run.published_count}</div>
                        </div>
                        {run.error_count > 0 && (
                          <div className="text-right text-sm">
                            <div className="text-destructive text-xs">Errors</div>
                            <div className="font-medium text-destructive">{run.error_count}</div>
                          </div>
                        )}
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* CR555: Pagination bar */}
            {!loading && filteredTotal > 0 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Showing {rangeStart}–{rangeEnd} of {filteredTotal}
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-xs text-muted-foreground">Per page:</Label>
                    <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                      <SelectTrigger className="h-8 w-[70px] text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAGE_SIZES.map((s) => (
                          <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground px-2">
                      Page {page} of {totalPages}
                    </span>
                    <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <CreateBatchRunWizard open={showCreateWizard} onOpenChange={setShowCreateWizard} onCreated={handleRunCreated} />
      <RulePacksManagerDialog open={showRulePacksManager} onOpenChange={setShowRulePacksManager} />

      {/* CR558: Hard Delete Confirmation Dialog with extra guards */}
      <Dialog open={hardDeleteDialogOpen} onOpenChange={(o) => { setHardDeleteDialogOpen(o); if (!o) { setHardDeleteConfirmText(""); setHardDeleteAcknowledged(false); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Permanently delete batch runs?
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 pt-2">
                <p>
                  This will permanently remove <strong>{selectAllMatching ? matchingCount : selectedIds.size}</strong> batch
                  run(s) from the database. This cannot be undone.
                </p>
                <p className="text-sm text-muted-foreground">
                  Note: This removes database records. Stored flyer assets may remain until lifecycle cleanup runs.
                </p>
                {(selectAllMatching ? matchingCount : selectedIds.size) > 200 && (
                  <p className="text-sm bg-destructive/10 p-2 rounded font-medium">
                    ⚠️ You are about to delete more than 200 runs. Please double-check this is intentional.
                  </p>
                )}
                {(selectAllMatching ? matchingCount : selectedIds.size) > 25 && (
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={hardDeleteAcknowledged}
                      onCheckedChange={(v) => setHardDeleteAcknowledged(v === true)}
                    />
                    I understand this is permanent
                  </label>
                )}
                <p className="text-sm">
                  Type <span className="font-mono font-bold">DELETE</span> to confirm:
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <Input
            value={hardDeleteConfirmText}
            onChange={(e) => setHardDeleteConfirmText(e.target.value)}
            placeholder="Type DELETE"
            className="font-mono"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setHardDeleteDialogOpen(false); setHardDeleteConfirmText(""); setHardDeleteAcknowledged(false); }}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={
                hardDeleteConfirmText !== "DELETE" ||
                bulkActionLoading ||
                ((selectAllMatching ? matchingCount : selectedIds.size) > 25 && !hardDeleteAcknowledged)
              }
              onClick={handleBulkHardDelete}
            >
              {bulkActionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Permanently Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
